import React, { useEffect } from 'react';
import AACChromeAPI from '@amzn/aac-chrome-api';
import { InlineMessage } from '@amzn/storm-ui';
import { LinkProps } from '@amzn/aac-chrome-internal-api';
import { getClickEventHandler } from 'src/commons/utils';
import didElementLoad from 'src/commons/utils/didElementLoad';
import { HelpDropdownRenderer } from '../../UtilityMenu/UtilityMenu';
import { ActionsButton } from './Button';
import { ActionsDropdown } from './Dropdown';
import { ActionsText } from './Text';
import { ActionsPopover } from './Popover';
import {
  Action,
  ACTION_TYPE,
  ActionInfo,
  ButtonType,
  DropdownType,
  PopoverType,
} from '../types';
import {
  ActionButtonsWrap,
  ActionItemWrap,
  ActionLinkItemWrap,
  ErrorMessageWrap,
} from './styles';

import { logger } from 'src/commons/utils/logger';
import { Container } from 'src/commons/components/Container';
import { PageHeaderBundleProvider } from 'src/providers/PantherLocalizationProvider';

export const Actions: React.VFC<{
  actions: ActionInfo[] | undefined;
  errorMessage?: string;
  isFocusPage?: boolean;
  helpInfo?: LinkProps[];
}> = ({ actions, errorMessage, isFocusPage, helpInfo }) => {
  useEffect(() => {
    if (actions && actions.length > 0) {
      didElementLoad('actions');
    }
  }, []);

  const actionsGroup =
    actions &&
    actions.map((actionTemplate) => {
      let item;
      let eventHandler;
      let action;
      switch (actionTemplate.type) {
        case ACTION_TYPE.BUTTON:
          action = actionTemplate as ButtonType;
          eventHandler = getClickEventHandler(
            action,
            AACChromeAPI.getActionEventTarget(),
          );
          item = (
            <ActionItemWrap
              key={action.id}
              data-ccx-e2e-id={`aac-action-${action.id}`}
            >
              <ActionsButton {...action} onClickEvent={eventHandler} />
            </ActionItemWrap>
          );
          break;
        case ACTION_TYPE.DROPDOWN:
          action = actionTemplate as DropdownType;
          item = (
            <ActionItemWrap
              key={action.id}
              data-ccx-e2e-id={`aac-action-${action.id}`}
            >
              <ActionsDropdown {...action} />
            </ActionItemWrap>
          );
          break;
        case ACTION_TYPE.POPOVER:
          action = actionTemplate as PopoverType;
          item = (
            <PageHeaderBundleProvider key={action.id}>
              <ActionItemWrap data-ccx-e2e-id={`aac-action-${action.id}`}>
                <ActionsPopover {...action} />
              </ActionItemWrap>
            </PageHeaderBundleProvider>
          );
          break;
        case ACTION_TYPE.TEXT:
          action = actionTemplate as Action;
          item = (
            <ActionItemWrap
              key={action.id}
              data-ccx-e2e-id={`aac-action-${action.id}`}
            >
              <ActionsText {...action} />
            </ActionItemWrap>
          );
          break;
        default:
          logger.error(
            `Unsupported Action type, returning empty item. type: ${actionTemplate.type}`,
          );
          item = '';
      }

      return item;
    });

  const buildHelpDropdown = isFocusPage && helpInfo?.length;

  return (
    <Container id="actions">
      <ActionButtonsWrap>
        {actionsGroup}
        {buildHelpDropdown && (
          <ActionLinkItemWrap>
            <HelpDropdownRenderer />
          </ActionLinkItemWrap>
        )}
      </ActionButtonsWrap>
      {errorMessage && (
        <ErrorMessageWrap>
          <InlineMessage
            messageType="error"
            message={errorMessage}
            style={{
              textAlign: 'right',
              marginTop: '12px',
              display: 'block',
            }}
          />
        </ErrorMessageWrap>
      )}
    </Container>
  );
};
